import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  Check,
  Copy,
  KeyRound,
  LoaderCircle,
  Plus,
  X,
} from "lucide-react";
import {
  useDataProvider,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { CrmDataProvider } from "../providers/types";
import type { CreatedMcpAccessToken, McpAccessToken } from "../types";

const TOKEN_QUERY_KEY = ["mcp-access-tokens"] as const;
const EXPIRATION_OPTIONS = [30, 90, 365];

type TokenStatus = "active" | "expired" | "revoked";

const getTokenStatus = (token: McpAccessToken): TokenStatus => {
  if (token.revoked_at) return "revoked";
  if (new Date(token.expires_at).getTime() <= Date.now()) return "expired";
  return "active";
};

export const McpAccessTokens = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [locale] = useLocaleState();
  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState("90");
  const [createdToken, setCreatedToken] =
    useState<CreatedMcpAccessToken | null>(null);
  const [copied, setCopied] = useState(false);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale || undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    [locale],
  );

  const {
    data: tokens = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: TOKEN_QUERY_KEY,
    queryFn: () => dataProvider.mcpAccessTokensList(),
  });

  const createToken = useMutation({
    mutationFn: () =>
      dataProvider.mcpAccessTokenCreate({
        name: name.trim(),
        expires_in_days: Number(expiresInDays),
      }),
    onSuccess: (token) => {
      setCreatedToken(token);
      setName("");
      queryClient.invalidateQueries({ queryKey: TOKEN_QUERY_KEY });
      notify("crm.profile.mcp.tokens.created", {
        messageArgs: { _: "MCP token created" },
      });
    },
    onError: (error) => {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    },
  });

  const revokeToken = useMutation({
    mutationFn: (id: string) => dataProvider.mcpAccessTokenRevoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TOKEN_QUERY_KEY });
      notify("crm.profile.mcp.tokens.revoked", {
        messageArgs: { _: "MCP token revoked" },
      });
    },
    onError: (error) => {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    },
  });

  const handleCreate = () => {
    if (!name.trim()) return;
    createToken.mutate();
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    await navigator.clipboard.writeText(createdToken.token);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const handleRevoke = (token: McpAccessToken) => {
    const confirmed = window.confirm(
      translate("crm.profile.mcp.tokens.revoke_confirm", {
        _: `Revoke token “${token.name}”? This cannot be undone.`,
        name: token.name,
      }),
    );
    if (confirmed) revokeToken.mutate(token.id);
  };

  const formatDate = (value: string) => dateFormatter.format(new Date(value));

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium">
          {translate("crm.profile.mcp.tokens.title", {
            _: "Personal access tokens",
          })}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {translate("crm.profile.mcp.tokens.description", {
            _: "Create a revocable token for an MCP client. The token has the same CRM permissions as your account.",
          })}
        </p>
      </div>

      {createdToken && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-medium">
                {translate("crm.profile.mcp.tokens.copy_now", {
                  _: "Copy this token now",
                })}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {translate("crm.profile.mcp.tokens.shown_once", {
                  _: "It will not be shown again after you close this message.",
                })}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => setCreatedToken(null)}
              aria-label={translate("ra.action.close", { _: "Close" })}
            >
              <X />
            </Button>
          </div>
          <code className="mt-3 block break-all rounded-md bg-background px-3 py-2 text-xs">
            {createdToken.token}
          </code>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={handleCopy}
          >
            {copied ? <Check /> : <Copy />}
            {copied
              ? translate("crm.common.copied")
              : translate("crm.common.copy")}
          </Button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem_auto]">
        <div className="space-y-1.5">
          <Label htmlFor="mcp-token-name">
            {translate("crm.profile.mcp.tokens.name", { _: "Token name" })}
          </Label>
          <Input
            id="mcp-token-name"
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleCreate();
              }
            }}
            placeholder={translate("crm.profile.mcp.tokens.name_placeholder", {
              _: "For example, Codex on Mac",
            })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="mcp-token-expiration">
            {translate("crm.profile.mcp.tokens.expires", { _: "Expires" })}
          </Label>
          <Select value={expiresInDays} onValueChange={setExpiresInDays}>
            <SelectTrigger id="mcp-token-expiration" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRATION_OPTIONS.map((days) => (
                <SelectItem key={days} value={String(days)}>
                  {translate("crm.profile.mcp.tokens.days", {
                    _: `${days} days`,
                    days,
                  })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          className="self-end"
          disabled={!name.trim() || createToken.isPending}
          onClick={handleCreate}
        >
          {createToken.isPending ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <Plus />
          )}
          {translate("crm.profile.mcp.tokens.create", { _: "Create" })}
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
            <LoaderCircle className="size-4 animate-spin" />
            {translate("ra.message.loading", { _: "Loading" })}
          </div>
        )}
        {isError && (
          <p className="p-4 text-sm text-destructive">
            {translate("crm.profile.mcp.tokens.load_error", {
              _: "Could not load MCP tokens.",
            })}
          </p>
        )}
        {!isLoading && !isError && tokens.length === 0 && (
          <div className="flex flex-col items-center gap-2 p-6 text-center text-sm text-muted-foreground">
            <KeyRound className="size-5" />
            {translate("crm.profile.mcp.tokens.empty", {
              _: "You have no personal MCP tokens yet.",
            })}
          </div>
        )}
        {tokens.map((token, index) => {
          const status = getTokenStatus(token);
          return (
            <div
              key={token.id}
              className={`flex items-start justify-between gap-3 p-3 ${
                index > 0 ? "border-t" : ""
              }`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate text-sm font-medium">
                    {token.name}
                  </span>
                  <Badge
                    variant={status === "active" ? "secondary" : "outline"}
                  >
                    {translate(`crm.profile.mcp.tokens.status.${status}`, {
                      _: status,
                    })}
                  </Badge>
                </div>
                <code className="mt-1 block text-xs text-muted-foreground">
                  {token.token_prefix}
                </code>
                <p className="mt-1 text-xs text-muted-foreground">
                  {translate("crm.profile.mcp.tokens.expires_at", {
                    _: `Expires ${formatDate(token.expires_at)}`,
                    date: formatDate(token.expires_at),
                  })}
                  {token.last_used_at && (
                    <>
                      {" · "}
                      {translate("crm.profile.mcp.tokens.last_used", {
                        _: `Last used ${formatDate(token.last_used_at)}`,
                        date: formatDate(token.last_used_at),
                      })}
                    </>
                  )}
                </p>
              </div>
              {status === "active" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground hover:text-destructive"
                  disabled={revokeToken.isPending}
                  onClick={() => handleRevoke(token)}
                  aria-label={translate("crm.profile.mcp.tokens.revoke", {
                    _: "Revoke token",
                  })}
                >
                  <Ban />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
